import React, { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, ArrowRight, Coins, RefreshCw } from 'lucide-react'
import { checkoutService } from '@/services/checkout'
import { useAuthStore, useUserRole } from '@/store/authStore'
import { useTerminology } from '@/hooks/useTerminology'
import styles from './CheckoutSuccess.module.css'

export function CheckoutSuccess() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const setUser = useAuthStore(state => state.setUser)

  const [creditBalance, setCreditBalance] = useState<number | null>(null)
  const [creditsAdded, setCreditsAdded] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const role = useUserRole()
  const t = useTerminology()
  const isCaptain = role === 'captain' || role === 'admin'

  const sessionId = searchParams.get('session_id')

  useEffect(() => {
    if (!sessionId) {
      navigate('/users/plans')
      return
    }

    verifyAndLoad()
  }, [sessionId])

  const updateCachedBalance = (newBalance: number) => {
    if (user) {
      setUser({ ...user, credit_balance: newBalance })
    }
  }

  const verifyAndLoad = async () => {
    try {
      const result = await checkoutService.verifySession(sessionId!)
      setCreditBalance(result.credit_balance)
      updateCachedBalance(result.credit_balance)
      if (result.credits_added) setCreditsAdded(result.credits_added)
    } catch {
      try {
        const balanceData = await checkoutService.getBalance()
        setCreditBalance(balanceData.credit_balance)
        updateCachedBalance(balanceData.credit_balance)
      } catch (_) {}
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-theme-bg">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-primary-subtle shadow-theme-glow">
            <CheckCircle className="w-12 h-12 text-theme-primary" />
          </div>

          <h1 className="text-3xl font-bold mb-3 text-theme-primary font-orbitron text-shadow-primary">
            CREDITS ADDED
          </h1>
          <p className="text-lg mb-8 max-w-xl mx-auto text-theme-text font-roboto-mono">
            Your purchase was successful. Credits have been added and are ready to use.
          </p>

          {/* Balance Display */}
          {creditBalance !== null && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-xl p-6 mb-8 max-w-sm mx-auto border-2 bg-surface border-theme-primary shadow-theme-glow"
            >
              {creditsAdded && (
                <p className="text-sm mb-2 text-theme-primary font-roboto-mono">
                  +{creditsAdded} credits added
                </p>
              )}
              <p className="text-xs uppercase tracking-wider mb-1 text-theme-text font-roboto-mono">Your Balance</p>
              <p className="text-4xl font-bold text-theme-primary font-orbitron text-shadow-primary">
                {creditBalance} <span className="text-base font-normal text-theme-text">credits</span>
              </p>
            </motion.div>
          )}

          {loading && (
            <div className="py-6">
              <RefreshCw className="w-6 h-6 mx-auto text-theme-primary animate-spin" />
            </div>
          )}

          {/* Next Steps */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-xl p-6 mb-8 max-w-xl mx-auto text-left border bg-surface border-theme-border"
          >
            <h2 className="text-lg font-bold mb-4 text-theme-secondary font-orbitron">NEXT STEPS</h2>

            <div className="space-y-4">
              {(isCaptain ? [
                { step: '1', title: `Run a ${t.course.lower}`, desc: `Set up ${t.lesson.lowerPlural} and invite students to participate.` },
                { step: '2', title: 'Credits deducted per run', desc: `Each ${t.course.lower} has tiered pricing based on the number of students.` },
                { step: '3', title: 'Buy more anytime', desc: 'Credits never expire. Purchase more whenever you need them.' },
              ] : [
                { step: '1', title: `Browse ${t.course.lowerPlural}`, desc: `Explore available ${t.course.lowerPlural} and find one that interests you.` },
                { step: '2', title: 'Enroll and start learning', desc: `Credits are deducted when you enroll in a ${t.course.lower}.` },
                { step: '3', title: 'Buy more anytime', desc: 'Credits never expire. Purchase more whenever you need them.' },
              ]).map(({ step, title, desc }) => (
                <div key={step} className="flex items-start gap-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${styles.stepBadge}`}>
                    <span className="text-xs font-bold text-theme-primary">{step}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-theme-secondary">{title}</h3>
                    <p className="text-sm text-theme-text">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-3 justify-center"
          >
            <button
              onClick={() => navigate('/dashboard')}
              className={`px-6 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 text-theme-on-primary ${styles.dashboardButton}`}
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => navigate('/users/plans')}
              className="px-6 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 border bg-transparent text-theme-secondary border-theme-secondary"
            >
              <Coins className="w-4 h-4" />
              Buy More Credits
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
